import axios from "axios";

import { API_ENDPOINTS, BASE_URL, getClientDomain } from "@/configs";
import { getDeviceId } from "@/lib/deviceId";
import { dispatch, login, store, takeLegacySessionToken } from "@/store";
import { User } from "@/types";

interface RefreshResponse {
  user: User | null;
  token: string | null;
}

// Plain axios, not apiRequest: a guest's empty answer must not go through the 401 sign-out.
const refresh = async (bearer?: string | null): Promise<boolean> => {
  const { data } = await axios.post<RefreshResponse>(
    `${BASE_URL}/api/${API_ENDPOINTS.AUTH.REFRESH}`,
    {},
    {
      withCredentials: true,
      timeout: 15000,
      headers: {
        "x-domain": getClientDomain(),
        "x-device-id": getDeviceId(),
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
    },
  );
  if (!data?.token || !data.user) {
    setSessionHint(false);
    return false;
  }
  dispatch(login({ user: data.user, token: data.token }));
  return true;
};

// Set while signed in, so a guest's first paint need not wait for the refresh call.
const SESSION_HINT_KEY = "skystake-had-session";

const setSessionHint = (signedIn: boolean) => {
  try {
    if (signedIn) localStorage.setItem(SESSION_HINT_KEY, "1");
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Blocked storage: every load waits for the restore, as before.
  }
};

let wasSignedIn = false;
store.subscribe(() => {
  const signedIn = store.getState().auth.isLoggedIn;
  if (signedIn !== wasSignedIn) setSessionHint(signedIn);
  wasSignedIn = signedIn;
});

let legacyToken: string | null | undefined;
const takeLegacyToken = () => (legacyToken ??= takeLegacySessionToken());

/** Whether this browser may hold a session the first render should wait for. */
export const mayHaveSession = (): boolean => {
  if (takeLegacyToken()) return true;
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === "1";
  } catch {
    return true;
  }
};

let restoring: Promise<void> | null = null;

/** Once per page load: the session back from the httpOnly cookie, if there is a live one. */
export const restoreSession = (): Promise<void> =>
  (restoring ??= refresh(takeLegacyToken())
    .then(() => undefined)
    .catch((error) => {
      // The site works signed out; the player can sign in again.
      console.warn("Session restore failed:", error);
    }));

/** Signs in with a session token handed over in a link, and stores the restore cookie for it. */
export const adoptSessionToken = (token: string): Promise<boolean> => refresh(token);
